import { Component } from '../lib/ComponentBase';
import { simpleComponent } from '../lib/ComponentHelpers';
import type { MoveProvider, DisplayProvider, DisplayInfo, PathfindingNode, WorldState } from '../types/Components';
import type { Entity } from '../types/Entity';
import { HexBearing } from '../grid';
import { ActionType, ActionMode } from '../lib/Action';
import Action from '../lib/Action';
import HexPosition from './HexPosition';

/**
 * An ability that allows an entity to turn directly to any bearing in one action.
 * It bids with a low priority to propose a TURN outcome.
 */
class Swivel extends Component implements MoveProvider, DisplayProvider {
  static supportedModes = [ActionMode.STANDARD];
  priority: number;

  constructor(data: { priority?: number } = {}) {
    super();
    this.priority = data.priority ?? 1;
  }

  getDisplayInfo(): DisplayInfo {
    return { icon: 'game-icons:cycle', label: 'Swivel', description: 'Instantly face any direction.' };
  }

  getPossibleMoves(fromNode: PathfindingNode, _worldState: { walkableCoords: Set<string> }): PathfindingNode[] {
    const { coords, bearing } = fromNode;
    const moves: PathfindingNode[] = [];
    for (const b of HexBearing.all) {
      if (b !== bearing) {
        moves.push({ coords, bearing: b });
      }
    }
    return moves;
  }

  getPossiblePlayerActions(entity: Entity, mode: typeof ActionMode[keyof typeof ActionMode], _worldState: WorldState): Map<string, Action> {
    const actions = new Map<string, Action>();
    if (mode !== ActionMode.STANDARD) return actions;

    const selfPosition = entity.getComponent(HexPosition);
    if (!selfPosition) return actions;

    const fromNode = { coords: selfPosition.coords, bearing: selfPosition.bearing };
    const possibleMoves = this.getPossibleMoves(fromNode, { walkableCoords: new Set() });

    // Offer a turn action for every direction except the one we're currently facing.
    for (const move of possibleMoves) {
      const neighborCoords = selfPosition.coords.neighbor(move.bearing);
      actions.set(neighborCoords.key, new Action(entity, ActionType.TURN, { bearing: move.bearing }));
    }

    return actions;
  }
}

export default simpleComponent(Swivel);
